/**
 * The left-nav action glyphs, as inline SVG.
 *
 * They used to be emoji. An emoji is a text glyph: it sits on the baseline of a line box whose
 * ascent/descent are decided by the font, so centring the *box* inside the 24px rounded button
 * still leaves the drawing visibly off-centre. An SVG has no baseline — the viewBox IS the box,
 * so `place-items:center` puts it exactly in the middle. Sizing and colour come from `.nav-ico`
 * in styles.css (`currentColor`, so a button's hover/disabled state carries through).
 *
 * All of them are decorative: the button around them owns the accessible name.
 */

interface IconProps {
  /** Extra class on the <svg>, e.g. to size one icon differently. */
  className?: string
}

const cls = (extra?: string): string => 'nav-ico' + (extra ? ' ' + extra : '')

export function IconSearch({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <line x1="15.5" y1="15.5" x2="20" y2="20" />
    </svg>
  )
}

export function IconFolder({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  )
}

export function IconPlus({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

export function IconPaste({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="9" y="3" width="6" height="3.5" rx="1" />
      <path d="M9 5H6.5A1.5 1.5 0 0 0 5 6.5v13A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 17.5 5H15" />
    </svg>
  )
}

/** Attach an image to a chat message (the composer's paperclip). */
export function IconAttach({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20 11.5 12.3 19.2a4.5 4.5 0 0 1-6.4-6.4l7.8-7.8a3 3 0 0 1 4.3 4.3l-7.8 7.8a1.5 1.5 0 0 1-2.1-2.1l7.2-7.2" />
    </svg>
  )
}

/** Waiting its turn (a queued chat prompt): a static hourglass, never animated. */
export function IconHourglass({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 3h12M6 21h12" />
      <path d="M7 3v2.5c0 2.5 5 4.5 5 6.5s-5 4-5 6.5V21M17 3v2.5c0 2.5-5 4.5-5 6.5s5 4 5 6.5V21" />
    </svg>
  )
}

export function IconClose({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  )
}

/**
 * A slides deliverable: two overlapping slides. Unlike the nav-button glyphs above it is sized to
 * the text (1em) and sits inline next to a deliverable's title, so it takes no `.nav-ico` class.
 */
export function IconDeck({ className }: IconProps = {}) {
  return (
    <svg className={className} viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
      {/* back slide (top-left): only the edges the front one leaves uncovered */}
      <path d="M12.5 5V2.5H1.5V10.5H4" />
      {/* front slide (bottom-right) */}
      <rect x="4" y="5" width="11" height="8" rx="0.6" />
    </svg>
  )
}

/** Focus mode (AB#282), off: four corner arrows pointing outward — "expand the editor". */
export function IconExpand({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9V4h5M4 4l6 6" />
      <path d="M20 9V4h-5M20 4l-6 6" />
      <path d="M4 15v5h5M4 20l6-6" />
      <path d="M20 15v5h-5M20 20l-6-6" />
    </svg>
  )
}

/** Focus mode, on: the same four arrows pointing inward — "restore the layout". */
export function IconContract({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M10 5v5H5M10 10 4 4" />
      <path d="M14 5v5h5M14 10l6-6" />
      <path d="M10 19v-5H5M10 14l-6 6" />
      <path d="M14 19v-5h5M14 14l6 6" />
    </svg>
  )
}

/** A template's layout (AB#464, Edit / Open a template): a page with a header band and two blocks. */
export function IconTemplateLayout({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <line x1="4" y1="8.5" x2="20" y2="8.5" />
      <rect x="7" y="12" width="4" height="6" />
      <rect x="13" y="12" width="4" height="6" />
    </svg>
  )
}

/** Rename. */
export function IconPen({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 20l1-4.5L16.5 4a2.1 2.1 0 0 1 3 3L8.5 19z" />
      <line x1="14.5" y1="6" x2="17.5" y2="9" />
    </svg>
  )
}

/** Set as default. */
export function IconStar({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.9l-5.2 2.8 1-5.9-4.3-4.1 5.9-.8z" />
    </svg>
  )
}

/** Send a file up: an arrow leaving a tray. */
export function IconUpload({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5" />
      <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
    </svg>
  )
}

/** Save a file: an arrow landing in a tray. */
export function IconDownload({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
    </svg>
  )
}

/** Past versions: a clock with an arrow turning back. */
export function IconHistory({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 12a8 8 0 1 0 2.6-5.9L4 8.5" />
      <path d="M4 4v4.5h4.5" />
      <path d="M12 8v4.5l3 1.8" />
    </svg>
  )
}

export function IconTrash({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16M9 7V4.5h6V7" />
      <path d="M6 7l1 13h10l1-13" />
      <path d="M10 11v5M14 11v5" />
    </svg>
  )
}
